//Our single file for defining all api routes
import { SqlFilterOperator, SqlFilterParameters, SqlSortDirection } from './sqlParams';
/**
 * Base API route for all API calls.
 */
export const ApiBaseRoot: string = process.env['NX_PUBLIC_SHELL_API_ENDPOINT']!;

/**
 * Base URL for the external onboarding backend (separate service, different origin).
 */
export const OnboardingApiBaseRoot: string = process.env['NX_PUBLIC_ONBOARDING_API_ENDPOINT']!;

/**
 * List of API routes.
 */
export enum ApiRoutes {
  PLUGINS = `/v2/plugins`,
  PLUGINS_V3 = `/v3/plugins`,
  PLUGINS_V3_SIDEBAR = `/v3/plugins/sidebar`,
  PLUGINS_V3_APP_CONFIG = `/v3/plugins/app-config`,
  COMPANIES = `/v2/companies`,
  COMPANIES_V3 = `/v3/companies`,
  COMPANIES_FILTER = `/v2/companies/filter`,
  COMPANIES_FILTER_V3 = `/v3/companies/filter`,
  COMPANIES_BY_NAME = `/v2/companies/getByName`,
  COMPANIES_BY_NAME_V3 = `/v3/companies/getByName`,
  COMPANIES_FILTER_OPTIONS = `/v2/companies/filters-options`,
  COMPANIES_FILTER_OPTIONS_V3 = `/v3/companies/filters-options`,
  COMPANIES_TARGET_MARKETS = `/v2/companies/target-markets`,
  COMPANIES_TARGET_MARKETS_V3 = `/v3/companies/target-markets`,
  COMPANIES_LISTING = `/v2/companies/listing`,
  COMPANIES_LISTING_V3 = `/v3/companies/listing`,
  COMPANIES_LISTING_FOR_INVITE_V3 = `/v3/companies/company-listing-for-invite`,
  COMPANIES_ONBOARDING_STATUS = `/v2/companies/{companyId}/onboarding-status`,
  COMPANIES_ONBOARDING_STATUS_V3 = `/v3/companies/{companyId}/onboarding-status`,
  DELETE_COMPANY = `/v2/companies/{companyId}`,
  DELETE_COMPANY_V3 = `/v3/companies/{companyId}`,
  SEARCH_COMPANIES = `/v1/companies/search`,
  LOCATIONS = `/v1/companies/{id}/locations`,
  LOCATIONS_V2 = `/v2/companies/{companyId}/locations`,
  LOCATIONS_V3 = `/v3/companies/{companyId}/locations`,
  LOCATIONS_V2_OFFERINGS = `/v2/companies/{companyId}/locations/offerings`,
  LOCATIONS_V3_OFFERINGS = `/v3/companies/{companyId}/locations/offerings`,
  LOCATIONS_TIMEZONES_V3 = `/v3/companies/{companyId}/locations/timezones`,
  LOCATIONS_YEXT_CATEGORIES_V2 = `/v2/companies/{companyId}/locations/yext-categories`,
  LOCATIONS_YEXT_CATEGORIES_V3 = `/v3/companies/{companyId}/locations/yext-categories`,
  DIVISIONS = `/v1/companies/{id}/divisions`,
  USERS = `/v1/users`,
  USERS_V2 = `/v2/users`,
  USERS_V3 = `/v3/users`,
  USER_V2_ROLES = `/v2/users/roles`,
  USER_V3_ROLES = `/v3/users/roles`,
  ROLES = `/v1/companies/{id}/roles`,
  UPLOAD = `/v1/filestore`,
  INVITE_USER = `/v1/invite`,
  INVITE_USER_V2 = `/v2/invite`,
  INVITE_USER_V3 = `/v3/invite`,
  INVITE_SYSTEMS_USER = `/v1/invite_systems_user`,
  INVITE_SYSTEMS_USER_V2 = `/v2/invite_systems_user`,
  INVITE_SYSTEMS_USER_V3 = `/v3/invite_systems_user`,
  MANAGE_COMPANIES_V2 = `/v2/users/{userId}/manage-companies`,
  MANAGE_COMPANIES_V3 = `/v3/users/{userId}/manage-companies`,
  UNINVITE_USER = `/v1/uninvite`,
  REMOVE_USER = `/v1/users/{id}`,
  SWITCH_TENANT = `/v2/switch_tenant`,
  SWITCH_TENANT_V3 = `/v3/switch_tenant`,
  USER_MEMBERSHIPS = `/v1/users/{id}/memberships`,
  COMPANY_MEMBERSHIPS = `/v1/companies/{id}/memberships`,
  COMPANY_USERS = `/v1/companies/{id}/users`,
  USER_COMPANIES = `/v1/users/{id}/companies`,
  USER_DETAILS = `/v1/users/{id}/details`,
  USER_DETAILS_V2 = `/v2/account/current-user/update`,
  NEXUS_RETAILERS = `/retailers`,
  NEXUS_RETAILERS_BY_NAME = `/retailersByName`,
  RESEND_INVITATION = `/v1/users/{id}/resend-invitation`,
  RESEND_INVITATION_V2 = `/v2/users/{userId}/resend-invitation`,
  RESEND_INVITATION_V3 = `/v3/users/{userId}/resend-invitation`,
  PENDING_TEMP_USER_COUNT = `/dhq-migration/temp-users/invite/{tenantId}/count`,
  COMPANY_LOCATIONS_BY_IDS = `/v2/companies/locations`,
  COMPANY_LOCATIONS_BY_IDS_V3 = `/v3/companies/locations`,
  COMPANY_LOCATIONS_BY_IDS_FOR_INVITE_V3 = `/v3/companies/locations-for-invite`,
  CURRENT_USER_V2 = `/v2/account/current-user`,
  CURRENT_USER_V3 = `/v3/account/current-user`,
  CURRENT_USER_UPDATE_V2 = `/v2/account/current-user/update`,
  CURRENT_USER_UPDATE_V3 = `/v3/account/current-user/update`,
  CURRENT_USER_COMPANIES_V2 = `/v2/account/current-user/companies`,
  CURRENT_USER_COMPANIES_V3 = `/v3/account/current-user/companies`,
  CURRENT_USER_LOCATIONS_V2 = `/v2/account/current-user/locations`,
  CURRENT_USER_LOCATIONS_V3 = `/v3/account/current-user/locations`,
  CURRENT_USER_DISABLE_LOCATION_V2 = `/v2/account/current-user/company/{companyId}/location/{locationId}`,
  CURRENT_USER_DISABLE_LOCATION_V3 = `/v3/account/current-user/company/{companyId}/location/{locationId}`,
  CURRENT_USER_MANAGE_LOCATION_V2 = `/v2/account/current-user/manage-location`,
  CURRENT_USER_MANAGE_LOCATION_V3 = `/v3/account/current-user/manage-location`,
  CURRENT_USER_LEAVE_COMPANY_V2 = `/v2/account/current-user/company/{id}`,
  CURRENT_USER_LEAVE_COMPANY_V3 = `/v3/account/current-user/company/{id}`,
  PLUGIN_FEATURES = `/v2/pluginFeatures`,
  PLUGIN_FEATURES_V3 = `/v3/pluginFeatures`,
  TENANT_PLUGIN_FEATURES = `/v2/tenantPluginsFeatures`,
  TENANT_PLUGIN_FEATURES_V3 = `/v3/tenantPluginsFeatures`,
  TENANT_PLUGIN_FEATURES_BATCH = `/v2/tenantPluginsFeatures/batch-get-all`,
  TENANT_PLUGIN_FEATURES_BATCH_V3 = `/v3/tenantPluginsFeatures/batch-get-all`,
  USERS_TO_INVITE_FOR_COMPANY = `/dhq-migration/temp-users/{tenantId}/users`,
  INVITE_TEMP_USERS = `/dhq-migration/temp-users/invite/temp-users`,
  USER_COMPANIES_V2 = `/v2/users/{userId}/companies`,
  USER_COMPANIES_V3 = `/v3/users/{userId}/companies`,
  USER_DETAILS_UPDATE_V2 = `/v2/users/{userId}/details`,
  USER_DETAILS_UPDATE_V3 = `/v3/users/{userId}/details`,
  USER_UPDATE_V2 = `/v2/users/{userId}`,
  USER_UPDATE_V3 = `/v3/users/{userId}`,
  USER_MEMBERSHIPS_V2 = `/v2/companies/{companyId}/memberships/{userId}`,
  USER_MEMBERSHIPS_V3 = `/v3/companies/{companyId}/memberships/{userId}`,
  USER_LOCATIONS_V2 = `/v2/users/{userId}/locations-with-access`,
  USER_LOCATIONS_V3 = `/v3/users/{userId}/locations-with-access`,
  MANAGE_USER_LOCATION_V2 = `/v2/users/{userId}/manage-location`,
  MANAGE_USER_LOCATION_V3 = `/v3/users/{userId}/manage-location`,
  TENANT_PLUGIN_FEATURES_BY_COMPANY = `/v2/tenant/{companyId}/plugin-features`,
  TENANT_PLUGIN_FEATURES_BY_COMPANY_V3 = `/v3/tenant/{companyId}/plugin-features`,
  NOTIFICATION_SETTINGS = `/v2/tenant-notification-settings/{tenantId}`,
  NOTIFICATION_SETTINGS_V3 = `/v3/tenant-notification-settings/{tenantId}`,
  NOTIFICATION_SETTINGS_TOGGLE = `/v2/tenant-notification-settings/{tenantId}/toggle`,
  NOTIFICATION_SETTINGS_TOGGLE_V3 = `/v3/tenant-notification-settings/{tenantId}/toggle`,
  NOTIFICATION_PREFERENCES = `/v2/tenant-notification-preferences/{tenantId}`,
  NOTIFICATION_PREFERENCES_V3 = `/v3/tenant-notification-preferences/{tenantId}`,
  NOTIFICATION_PREFERENCES_ITEM = `/v2/tenant-notification-preferences/{id}`,
  NOTIFICATION_PREFERENCES_ITEM_V3 = `/v3/tenant-notification-preferences/{id}`,
  NOTIFICATION_PREFERENCES_BULK_UPDATE = `/v2/tenant-notification-preferences/{tenantId}/bulk-update`,
  NOTIFICATION_PREFERENCES_BULK_UPDATE_V3 = `/v3/tenant-notification-preferences/{tenantId}/bulk-update`,
  NOTIFICATION_PREFERENCES_RESET = `/v2/tenant-notification-preferences/{tenantId}/reset`,
  NOTIFICATION_PREFERENCES_RESET_V3 = `/v3/tenant-notification-preferences/{tenantId}/reset`,
  INAPP_NOTIFICATIONS = `/v2/inapp-notifications/{tenantId}`,
  INAPP_NOTIFICATIONS_V3 = `/v3/inapp-notifications/{tenantId}`,
  INAPP_NOTIFICATION_MARK_READ = `/v2/inapp-notifications/{notificationJobId}/read`,
  INAPP_NOTIFICATIONS_MARK_ALL_READ = `/v2/inapp-notifications/{tenantId}/read-all`,
  INAPP_NOTIFICATION_MARK_READ_V3 = `/v3/inapp-notifications/{notificationJobId}/read`,
  ROLES_V3 = `/v3/roles`,
  USER_ROLES_V3 = `/v3/user-roles`,
  COMPANY_MEMBERSHIPS_UPDATE_V3 = `/v3/companies/{companyId}/memberships/{userId}`,
  TENANT_PLUGIN_FEATURES_WITH_PERMISSIONS = `/v3/tenant/{companyId}/plugin-features-permissions`,
  DEALER_GROUPS = `/v2/dealer-groups`,
  DEALER_GROUPS_V3 = `/v3/dealer-groups`,
  AI_ONBOARDING_LINKS_GENERATE = `/v2/ai-onboarding-links/{companyId}`,
  AI_ONBOARDING_LINKS_GENERATE_V3 = `/v3/ai-onboarding-links/{companyId}`,
  BULK_RESEND_INVITE = `/v2/users/bulk-resend-invite`,
  FEATURE_FLAG = `/v2/feature-flags`,
  BULK_RESEND_INVITE_V3 = `/v3/users/bulk-resend-invite`,
}

/**
 * API routes for the external onboarding service (uses OnboardingApiBaseRoot, not ApiBaseRoot).
 */
export enum OnboardingApiRoutes {
  AI_ONBOARDING_SYNC_CMS_TOKENS = `/onboarding/v1/businesses/{companyId}/sync-cms-tokens`,
}

/**
 * Get the full API route as a string.
 * @param route - The route to get the full URL for.
 * @returns The full API route as a string.
 */
export const getFullApiRoute = (route: any) => {
  return String(`${ApiBaseRoot}${route}`);
};

/**
 * Replace the route params with the values passed in.
 * @param route - The route to replace the params for.
 * @param params - The params to replace the route with.
 * @returns The route with the params replaced.
 */
export const replaceRouteParams = (
  route: string,
  params: { [key: string]: string | number | string[] | number[] },
): string => {
  let newRoute = route;
  Object.keys(params).forEach((key: string) => {
    newRoute = newRoute.replace(`{${key}}`, String(params[key]));
  });
  return newRoute;
};

export interface ApiParams {
  page?: number;
  per_page?: number;
  sortField?: string | string[];
  sortDir?: SqlSortDirection;
  query?: SqlFilterParameters[];
  searchKeyword?: string;
}

export { SqlFilterOperator, SqlSortDirection };

export const constructApiParams = (
  apiUrl: string,
  params?: ApiParams,
  useLimitPageParam?: boolean,
): string => {
  const urlParams = new URLSearchParams();

  if (params) {
    if (params.page) {
      urlParams.append('page', String(params.page));
    }
    if (params.per_page) {
      if (useLimitPageParam) urlParams.append('limit', String(params.per_page));
      else urlParams.append('per_page', String(params.per_page));
    }
    if (params.query) {
      params.query.forEach((q) => {
        urlParams.append(`${q.column}[${q.operator}]`, q.value.toString());
      });
    }
    if (params.sortField && params.sortDir) {
      //If we have an array of sort fields, we need to loop through them and append each one, else just append the single sort field
      if (Array.isArray(params.sortField)) {
        params.sortField.forEach((field) => {
          urlParams.append('sort', `${field}[${params.sortDir}]`);
        });
      } else {
        urlParams.append('sort', `${params.sortField}[${params.sortDir}]`);
      }
    }
    if (params.searchKeyword) {
      urlParams.append('search', params.searchKeyword);
    }
  }

  //If we have any url params, append them to the url else just return the url
  return urlParams.toString() ? apiUrl + '?' + urlParams.toString() : apiUrl;

  //return apiUrl + '?' + urlParams.toString();
};
export const constructNexusApiParams = (apiUrl: string, params?: ApiParams): string => {
  const urlParams = new URLSearchParams();

  if (params) {
    if (params.page) {
      urlParams.append('page', String(params.page));
    }
    if (params.per_page) {
      urlParams.append('per_page', String(params.per_page));
    }
    if (params.query) {
      params.query.forEach((q) => {
        urlParams.append(`${q.column}`, q.value.toString());
      });
    }
    if (params.sortField && params.sortDir) {
      urlParams.append('sort', `${params.sortField}[${params.sortDir}]`);
    }
  }

  //If we have any url params, append them to the url else just return the url
  return urlParams.toString() ? apiUrl + '?' + urlParams.toString() : apiUrl;

  //return apiUrl + '?' + urlParams.toString();
};
